import Header from "../components/Header";
import { motion } from "motion/react";
import { routeVariants } from "../routesVariants";
import {
  Modal,
  ModalClose,
  ModalHeader,
  Snackbar,
  Spinner,
  Text,
  Title,
} from "../uikit";
import Card from "../components/Card";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { DOMAIN } from "../consts";
import styles from "./changeoutlet.module.css";
import CrossIcon from "../img/cross.svg";
import { formatServerCityLabel, getCountryEmoji, sortServersForDisplay } from "../utils/serverDisplay";
import { getToken } from "../utils/tokenStorage";

export default function ChangeOutlet() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { subscription, connection, mode } = location.state || {};
  const isSubscriptionMode = mode === "subscription";
  const isOnlineMode = mode === "online";
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [servers, setServers] = useState(null);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [selectedServer, setSelectedServer] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);

  // Страницу можно открыть напрямую, без location.state (ссылка, refresh) —
  // тогда уводим на /account из эффекта: navigate во время рендера даёт
  // вложенные обновления и падение с React error #185.
  const hasRequiredState =
    !!subscription && (isSubscriptionMode || isOnlineMode);

  useEffect(() => {
    if (!hasRequiredState) {
      navigate("/account", { replace: true });
    }
  }, [hasRequiredState, navigate]);

  const backPath = isSubscriptionMode ? "/account" : "/account/online";
  const pageTitle = isSubscriptionMode
    ? t("Change exit server")
    : t("Server changing");

  const sortedServers = useMemo(
    () =>
      sortServersForDisplay(servers || [], {
        preferredServerName: subscription?.last_exit_server_name,
      }),
    [servers, subscription?.last_exit_server_name],
  );

  const getServers = async (token) => {
    const params = new URLSearchParams({ purpose: "output" });
    if (!isSubscriptionMode && connection?.local_ip) {
      params.set("local_ip", connection.local_ip);
    }
    const response = await fetch(
      DOMAIN + "/subscriptions/" + subscription.id + "/servers?" + params.toString(),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    ).then((r) => r.json());
    if (response.success) {
      const data = [...(response.servers || [])];
      setServers(data);
      if (data.length === 1) {
        setSelectedServer(data[0]);
        setIsModalOpened(true);
      }
      return true;
    }
    return false;
  };

  const init = async () => {
    setIsInitialLoading(true);
    const token = getToken();
    if (token == "" || token == null) {
      navigate("/login");
      return;
    }
    if (!hasRequiredState) {
      return;
    }
    if (!isSubscriptionMode && !connection?.local_ip) {
      navigate("/account/online", {
        state: { subscription },
      });
      return;
    }
    const getServersResult = await getServers(token);
    if (!getServersResult) {
      navigate("/account");
    }
    setIsInitialLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const setOutlet = async (serverName = "", address = "") => {
    closeModal();
    setIsInitialLoading(true);
    const token = getToken();
    if (token == "" || token == null) {
      navigate("/login");
      return;
    }

    const body = {};
    if (!isSubscriptionMode) {
      body.local_ip = connection.local_ip;
    }
    if (serverName != "") {
      body.exit_server = serverName;
    }
    if (address != "") {
      body.exit_ip = address;
    }

    const endpoint = isSubscriptionMode ? "/change-exit" : "/change";
    const response = await fetch(
      DOMAIN + "/subscriptions/" + subscription.id + endpoint,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }
    ).then((r) => r.json());

    const isSuccess = isSubscriptionMode
      ? response.success && response.changed
      : response.success;

    if (isSuccess) {
      navigate(backPath, {
        state: { subscription },
      });
    } else {
      setShowSnackbar(true);
    }

    setIsInitialLoading(false);
  };

  const closeModal = () => {
    setIsModalOpened(false);
    setSelectedServer(null);
  };

  if (!hasRequiredState) {
    return null;
  }

  return (
    <motion.div
      className="container background-regular border-padding column-spacing column"
      variants={routeVariants}
      initial="initial"
      animate="final"
      exit="exit"
    >
      <Header
        isBack={true}
        backCallback={() =>
          navigate(backPath, {
            state: { subscription },
          })
        }
      >
        <Title level="1" weight="3">
          {pageTitle}
        </Title>
      </Header>
      {isInitialLoading ? (
        <div className="center full-height">
          <Spinner />
        </div>
      ) : (
        <>
          <Card>
            <div className="column">
              {sortedServers.length &&
                sortedServers.map((row) => {
                  return (
                    <div
                      className={"prow ph-05 " + styles["country-row"]}
                      key={row.id}
                      onClick={() => {
                        setSelectedServer(row);
                        setIsModalOpened(true);
                      }}
                    >
                      <div>{formatServerCityLabel(row, i18n.resolvedLanguage)}</div>
                    </div>
                  );
                })}
            </div>
          </Card>
        </>
      )}
      <Modal
        header={
          <ModalHeader
            after={
              <ModalClose>
                <img
                  src={CrossIcon}
                  alt=""
                  className="monochrome-icon pointer"
                />
              </ModalClose>
            }
          ></ModalHeader>
        }
        onOpenChange={(isOpened) => {
          if (!isOpened) {
            closeModal();
          }
        }}
        open={isModalOpened}
      >
        <div>
          <div className="row">
            <Text>{t("Select an output IP address")}</Text>
          </div>
          <div
            className={"prow ph-05 " + styles["country-row"]}
            key={"any"}
            onClick={() => setOutlet(selectedServer.name, "")}
          >
            <div>
              {(() => {
                const emoji = getCountryEmoji(selectedServer?.country_code);
                const text = t("Any address");
                return emoji ? `${emoji}\u00A0\u00A0${text}` : text;
              })()}
            </div>
          </div>
          {selectedServer &&
            (selectedServer.outlets || []).map((outlet) => (
              <div
                className={"prow ph-05 " + styles["country-row"]}
                key={outlet.id}
                onClick={() => setOutlet(selectedServer.name, outlet.address)}
                style={{ gap: "16px" }}
              >
                <div>
                  {(() => {
                    const emoji = getCountryEmoji(selectedServer.country_code);
                    const text = outlet.address;
                    return emoji ? `${emoji}\u00A0\u00A0${text}` : text;
                  })()}
                </div>
                {outlet.is_private ? (
                  <div style={{ color: "var(--green)" }}>{t("Private")}</div>
                ) : null}
              </div>
            ))}
        </div>
      </Modal>
      {showSnackbar && (
        <Snackbar onClose={() => setShowSnackbar(false)}>
          {t("Something went wrong")}
        </Snackbar>
      )}
    </motion.div>
  );
}
